/**
 * "Needs a decision" (16 Oct): the dispatcher picks the team (and person) in
 * one go. The decision is logged and saved as a register rule: proposed to IT
 * management, or applied at once (ASSIGN_DECISION_MODE). With a ticket, it
 * can also be reassigned there in the same click (reason: wrong team).
 */

import { Alert, Button, Checkbox, Field, Input, Typography } from "@norican/react";
import { type FormEvent, useState } from "react";

import { ErrorState, SectionCard } from "@/components/common";
import { useT } from "@/i18n";
import type { AssignSuggestionResponse } from "@/lib/api";

import { useReassign, useRoutingDecision } from "./api";
import { splitList } from "./lib";
import { TeamPersonFields, type TeamPersonValue, useChosen } from "./team-person-fields";

export function DecisionPanel({ answer }: { answer: AssignSuggestionResponse }) {
  const { t } = useT("tickets");
  const facts = {
    kind: answer.decision_facts?.kind ?? "any",
    service: answer.decision_facts?.service ?? "",
    category: answer.decision_facts?.category ?? "",
    offering: answer.decision_facts?.offering ?? "",
    keywords: answer.decision_facts?.keywords ?? [],
  };
  const ticket = answer.ticket ?? null;
  const decide = useRoutingDecision();
  const reassign = useReassign(ticket?.ticket_id ?? "");
  const [value, setValue] = useState<TeamPersonValue>({ teamId: "", personId: "" });
  const [keywords, setKeywords] = useState(facts.keywords.join(", "));
  const [assignToo, setAssignToo] = useState(answer.can_reassign === true);
  const { team, person } = useChosen(value);
  const typed = facts.service !== "" || facts.category !== "" || facts.offering !== "";
  const words = splitList(keywords);
  const ready = team !== null && (typed || words.length > 0);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready) {
      return;
    }
    await decide.mutateAsync({
      ticket_number: ticket?.number ?? null,
      keywords: words,
      kind: facts.kind,
      service: facts.service,
      category: facts.category,
      offering: facts.offering,
      team: team.name,
      person: person ? { name: person.name, email: person.email, rec_id: person.id } : null,
      confidence: answer.suggestion?.confidence ?? 0,
    });
    if (assignToo && ticket !== null) {
      await reassign.mutateAsync({
        team_id: value.teamId,
        owner_id: value.personId === "" ? null : value.personId,
        reason: "wrong_team",
        note: null,
      });
    }
  }

  const saved = decide.data;
  return (
    <SectionCard title={t("itAdmin.assign.decision.title")}>
      <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-3">
        <Typography variant="body-sm" tone="muted">
          {t("itAdmin.assign.needsDecisionBody")}
        </Typography>
        {decide.error == null ? null : <ErrorState error={decide.error} variant="inline" />}
        {reassign.error == null ? null : <ErrorState error={reassign.error} variant="inline" />}
        {saved ? (
          <Alert tone="success">
            {saved.outcome === "applied"
              ? t("itAdmin.assign.decision.savedApplied")
              : t("itAdmin.assign.decision.savedProposed")}
            {reassign.data
              ? ` ${t("itAdmin.assign.decision.assigned", { team: reassign.data.team })}`
              : ""}
          </Alert>
        ) : null}
        <TeamPersonFields
          idPrefix="decision"
          value={value}
          onChange={setValue}
          personLabel={t("itAdmin.assign.decision.person")}
          personNone={t("itAdmin.assign.decision.personNone")}
        />
        <Field
          name="decision-keywords"
          label={t("itAdmin.assign.decision.keywords")}
          hint={t("itAdmin.assign.decision.keywordsHint")}
          required={!typed}
        >
          <Input
            value={keywords}
            onChange={(event) => setKeywords(event.target.value)}
            maxLength={300}
          />
        </Field>
        {answer.can_reassign === true ? (
          <Checkbox.Root
            checked={assignToo}
            onCheckedChange={(next) => setAssignToo(next === true)}
            label={t("itAdmin.assign.decision.assignToo")}
          />
        ) : null}
        <div>
          <Button
            type="submit"
            loading={decide.isPending || reassign.isPending}
            disabled={!ready || saved !== undefined}
          >
            {t("itAdmin.assign.decision.save")}
          </Button>
        </div>
      </form>
    </SectionCard>
  );
}
